// frontend/src/components/hr-case-management/utils/runToCase.ts
//
// The one-way bridge from a stored AI Employee run into the Live Queue's `HRCase`
// shape. Plan 05 section 5.
//
// ONE WAY, AND ONLY ONE WAY. The case store stays frontend-only: it powers an
// existing demo beat, it works, and nothing here rewrites it onto the API. Runs
// are read once on mount and merged ahead of the mocks, so the queue opens on the
// work the AI Employee actually did with the mock backlog still behind it.
//
// Everything below comes from a recorded field. An HRCase has required fields a
// run was never going to fill, and where a run holds no answer this leaves the
// field EMPTY and lets each surface present the gap: a labelled field in the case
// detail says "Not recorded", a queue column simply shows nothing. What it never
// does is invent. A fabricated department or a made-up SLA breach, in a panel
// whose whole claim is "this is the audit trail", would cost more than a blank.

import type {
  AutoRunDetail,
  AutoRunListItem,
  AutoRunStep,
  AutoRunMessage,
} from '@/components/hr-auto/types'
import { systemLabel } from '@/components/hr-auto/SystemBadge'
import type {
  AgentAction,
  ChatMessage,
  CaseStatus,
  CaseType,
  HRCase,
  PolicyTrigger,
} from '../types/case.types'

/** What a run carries when the list endpoint embeds its children, and when it does not. */
type BridgeableRun = AutoRunListItem & Partial<AutoRunDetail>

const NOT_RECORDED = 'Not recorded'

/** Plan 05 section 5, the scenario mapping, verbatim. */
const SCENARIO_TO_CASE_TYPE: Record<string, CaseType> = {
  requisition: 'multi_system_transaction',
  onboarding: 'entitlement_eligibility',
  offboarding: 'multi_system_transaction',
  insight: 'performance_data_request',
}

const RUN_STATUS_TO_CASE_STATUS: Record<string, CaseStatus> = {
  completed: 'resolved',
  awaiting_approval: 'awaiting_approval',
}

/**
 * Did a person decide anything on this run.
 *
 * THREE ANSWERS, NOT TWO. `recorded` is an approval, an edit or a cancellation.
 * `none` is the run saying it reached its outcome on its own. `unknown` is the
 * payload never carrying `decisions_taken` at all, which the list endpoint did
 * until its schema caught up.
 *
 * Collapsing `unknown` into `none` is what made an imported run read as L1 and
 * autonomous on the strength of a field nobody sent, in a queue whose whole claim
 * is that it shows what actually happened. When the field is missing, two
 * recorded facts still answer the question: a run parked at an approval is
 * waiting on a person by definition, and an artifact that left `draft` left it
 * because a person moved it.
 */
type DecisionEvidence = 'recorded' | 'none' | 'unknown'

function humanDecisionEvidence(run: BridgeableRun): DecisionEvidence {
  if (Array.isArray(run.decisions_taken)) {
    return run.decisions_taken.length > 0 ? 'recorded' : 'none'
  }
  if (run.status === 'awaiting_approval') return 'recorded'
  if ((run.artifacts ?? []).some((a) => a.approved_by || a.status !== 'draft')) return 'recorded'
  return 'unknown'
}

/**
 * The department, when and only when the run recorded one.
 *
 * WHAT A RUN ACTUALLY KNOWS. `GET /api/hr-auto/runs` returns `requisition_id`,
 * `employee_id` and `separation_id`: ids into other modules, not a directorate
 * name. The department a requisition lands against is resolved server side while
 * the run is written and never comes back on the row. The one place a run can
 * hold the name itself is its captured slots, so that is the only place this
 * looks, and today's scenarios capture grade, location and contract type rather
 * than a directorate, which means it usually finds nothing.
 *
 * Finding nothing returns an empty string, and every surface in this module
 * renders an empty department as no value at all. "Not recorded" was printed in
 * a queue column where every neighbouring row carries a real directorate, so the
 * words read as a department rather than as the absence of one. Blank reads as
 * what it is. The alternative, borrowing the requester's own department or the
 * scenario's authored "Policy", would be putting a person in a directorate on no
 * evidence, on the screen whose whole claim is that it shows what happened.
 */
function departmentFromRun(run: BridgeableRun): string {
  const slots = run.slots ?? {}
  const recorded = slots.directorate ?? slots.department
  return typeof recorded === 'string' ? recorded.trim() : ''
}

function toAgentActions(steps: AutoRunStep[], startedAt: string): AgentAction[] {
  return steps.map((step) => ({
    // The operator id is the Agent Hub's own name for the thing that ran the
    // step. Without one, the system it ran in is the honest answer.
    agentName: step.operator_id ?? `${systemLabel(step.system)} agent`,
    action: step.label,
    timestamp: step.created_at ?? startedAt,
    systemTouched: systemLabel(step.system),
    outcome: step.status === 'blocked' ? 'failed' : step.status === 'done' ? 'success' : 'pending',
    detail: step.detail ?? step.result_summary ?? step.action,
  }))
}

function toChatHistory(
  messages: AutoRunMessage[],
  requesterName: string,
  startedAt: string
): ChatMessage[] {
  return messages
    .filter((m) => m.role === 'user' || m.role === 'ai_employee')
    .sort((a, b) => a.sequence - b.sequence)
    .map((m) => ({
      id: m.id,
      sender: m.role === 'user' ? 'employee' : 'ai_agent',
      senderName: m.role === 'user' ? requesterName : 'Corporate Services coworker',
      message: m.content,
      timestamp: m.created_at ?? startedAt,
    }))
}

/**
 * The two-line stand-in for a run whose messages the list endpoint did not embed.
 *
 * Both lines are the run's OWN recorded text, the title it was given and the
 * outcome it wrote, so the case detail opens on something true rather than on an
 * empty conversation. The full transcript lives in the Operations Hub audit
 * panel, which is where plan 05 section 4.4 puts it.
 */
function fallbackChatHistory(run: BridgeableRun, requesterName: string): ChatMessage[] {
  const startedAt = run.started_at ?? new Date().toISOString()
  const history: ChatMessage[] = [
    {
      id: `${run.id}-request`,
      sender: 'employee',
      senderName: requesterName,
      message: run.title,
      timestamp: startedAt,
    },
  ]

  if (run.outcome_summary) {
    history.push({
      id: `${run.id}-outcome`,
      sender: 'ai_agent',
      senderName: 'Corporate Services coworker',
      message: run.outcome_summary,
      timestamp: run.completed_at ?? startedAt,
    })
  }

  return history
}

function toPolicyTriggers(run: BridgeableRun): PolicyTrigger[] {
  return (run.policies_applied ?? []).map((policy, index) => ({
    policyId: policy.id ?? `${run.run_code}-policy-${index + 1}`,
    policyName: policy.name,
    triggered: true,
    outcome:
      policy.outcome === 'escalate'
        ? 'escalate'
        : policy.outcome === 'approval_required'
          ? 'approval_required'
          : 'pass',
    detail: policy.detail ?? 'Applied during the run.',
  }))
}

/** Map one run onto one case. */
export function runToCase(run: BridgeableRun): HRCase {
  const startedAt = run.started_at ?? new Date().toISOString()
  const requesterName = run.initiated_by_name || run.initiated_by || NOT_RECORDED
  const messages = run.messages ?? []
  const steps = run.steps ?? []

  // `HRCase` has two tiers and three pathways and no way to say "not recorded",
  // so the unknown case takes the claim that cannot overstate the automation:
  // only a run that positively recorded no human decision is called autonomous.
  const evidence = humanDecisionEvidence(run)
  const autonomous = evidence === 'none'

  return {
    // Prefixed so a run can never collide with a mock case id.
    id: `auto-${run.id}`,
    caseRef: run.run_code,
    tier: autonomous ? 'L1' : 'L2',
    type: SCENARIO_TO_CASE_TYPE[run.scenario_key] ?? 'multi_system_transaction',
    status: RUN_STATUS_TO_CASE_STATUS[run.status] ?? 'investigating',
    channel: 'portal',
    submittedAt: startedAt,
    resolvedAt: run.completed_at ?? undefined,
    // A run records no service-level target, so the deadline mirrors the run's
    // own clock and nothing imported here is ever shown as breached. Inventing a
    // breach would be inventing a failure.
    slaDeadline: run.completed_at ?? startedAt,
    slaBreached: false,
    employee: {
      id: run.employee_id ?? run.id,
      name: requesterName,
      email: run.initiated_by ?? '',
      // Empty is this bridge's way of saying the run recorded nothing here. The
      // case detail's labelled fields spell that out as "Not recorded"; the
      // queue's columns leave it blank. Neither of them invents a value.
      department: departmentFromRun(run),
      location: '',
      employeeId: run.employee_id ?? '',
      manager: '',
      startDate: '',
    },
    subject: run.title,
    description: run.outcome_summary ?? run.title,
    resolutionPathway: autonomous ? 'autonomous' : 'approval_required',
    policyTriggers: toPolicyTriggers(run),
    agentActions: toAgentActions(steps, startedAt),
    resolutionSummary: run.outcome_summary ?? undefined,
    relatedSystems: (run.systems_touched ?? []).map(systemLabel),
    chatHistory:
      messages.length > 0
        ? toChatHistory(messages, requesterName, startedAt)
        : fallbackChatHistory(run, requesterName),
  }
}

/** Map a page of runs, newest first, ready to sit in front of the mocks. */
export function runsToCases(runs: BridgeableRun[]): HRCase[] {
  return runs.map(runToCase)
}
